/* ==========================================================================
   NAJIBS — hero media
   Owns <najibs-hero-media> for sections/hero.liquid. Background motion is opt-in
   by the browser: it never starts under prefers-reduced-motion, it stops the
   moment that preference changes, and the external frame only receives its src
   when playback is actually allowed. Loaded with defer from the section, so the
   theme editor re-runs the element's own lifecycle on every re-render.

   Two of those opt-ins are about phones. A narrow screen is given the small
   rendition the section rendered next to the full one, and a visitor whose
   browser reports Save-Data gets the still image with the play control left
   showing, so starting the video stays their decision rather than their bill.

   It also holds the first-screen sequence back until the entrance curtain has
   actually parted — see the note on that block below.
   ========================================================================== */

/* ---------------------------------------------------------------- sequence
   The hero reveal is the second half of the entrance, not a separate event.
   global.js is loaded in <head> and boots before this file, so by the time the
   entrance overlay is still counting up, every reveal target on the first
   screen has already been marked .is-visible behind it — the wipe crosses the
   picture and the type arrives while a full-screen curtain is over the top,
   and the visitor gets a finished, static page the moment they enter.

   This is not a second observer. It takes .is-visible back off the hero's own
   targets while the overlay is up, and entrance.js re-runs NAJIBS.initReveals
   the instant it finishes, which puts them back through the one shared
   observer with their delays untouched. It costs a class removal and two
   listeners, and it does nothing at all when the entrance is not showing. */
(function () {
  const html = document.documentElement;
  if (!html.classList.contains('entrance-on')) return;
  /* Under reduced motion there is no sequence to hold back: the page is already
     rendered still and complete, and the entrance resolves at once. */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const heroes = Array.prototype.slice.call(document.querySelectorAll('.section--hero'));
  if (!heroes.length) return;

  const strip = () => {
    heroes.forEach((hero) => {
      hero.querySelectorAll('[data-animate].is-visible').forEach((el) => el.classList.remove('is-visible'));
    });
  };

  const release = () => {
    heroes.forEach((hero) => {
      if (window.NAJIBS && typeof window.NAJIBS.initReveals === 'function') {
        window.NAJIBS.initReveals(hero);
      } else {
        hero.querySelectorAll('[data-animate]').forEach((el) => el.classList.add('is-visible'));
      }
    });
  };

  strip();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', strip, { once: true });
  }

  document.addEventListener('najibs:entered', release, { once: true });

  /* A back/forward restore drops the overlay without dispatching that event.
     Nothing may leave the first screen blank, so the class is checked again. */
  window.addEventListener('pageshow', () => {
    if (!html.classList.contains('entrance-on')) release();
  });
})();

(function () {
  if (customElements.get('najibs-hero-media')) return;

  class NajibsHeroMedia extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.frame = this.querySelector('iframe[data-src]');
      this.toggle = this.querySelector('[data-hero-video-toggle]');
      if (!this.video && !this.frame) return;

      this.source = this.video ? this.video.querySelector('source') : null;
      this.fullSrc = this.source ? this.source.getAttribute('src') : '';

      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.phoneQuery = window.matchMedia('(max-width: 749px)');
      this.onMotionChange = () => this.sync();

      if (this.toggle) {
        this.onToggle = () => {
          if (this.playing) this.stop(false);
          else this.start();
        };
        this.toggle.addEventListener('click', this.onToggle);
      }

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.onMotionChange);
      }
      this.sync();
    }

    disconnectedCallback() {
      if (this.motionQuery && this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.onMotionChange);
      }
    }

    sync() {
      if (this.motionQuery.matches) {
        this.stop(true);
      } else if (this.saveData()) {
        /* Not a refusal — the still image holds, and stop(false) leaves the
           control on screen so the visitor can start the video themselves. */
        this.stop(false);
      } else {
        this.start();
      }
    }

    saveData() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      return !!(connection && connection.saveData);
    }

    /* Swap in the phone rendition before the first byte is fetched. preload is
       "none", so nothing has been requested yet and load() costs nothing; the
       guard means a second call never restarts playback. */
    resolveSource() {
      if (!this.source) return;
      const small = this.video.dataset.srcSmall || '';
      if (!small) return;
      const wanted = this.phoneQuery.matches ? small : this.fullSrc;
      if (!wanted || this.source.getAttribute('src') === wanted) return;
      this.source.setAttribute('src', wanted);
      this.video.load();
    }

    start() {
      if (this.motionQuery.matches) return;
      if (this.frame) {
        const wanted = this.frame.dataset.src || '';
        if (wanted && this.frame.getAttribute('src') !== wanted) this.frame.setAttribute('src', wanted);
      }
      if (this.video) {
        this.resolveSource();
        const played = this.video.play();
        if (played && played.catch) played.catch(() => {});
      }
      this.playing = true;
      this.setAttribute('data-video-playing', 'true');
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.setAttribute('aria-label', this.toggle.dataset.pauseLabel || '');
      }
    }

    stop(hideControl) {
      if (this.frame && this.frame.getAttribute('src')) this.frame.removeAttribute('src');
      if (this.video) this.video.pause();
      this.playing = false;
      this.setAttribute('data-video-playing', 'false');
      if (this.toggle) {
        this.toggle.hidden = hideControl === true;
        this.toggle.setAttribute('aria-label', this.toggle.dataset.playLabel || '');
      }
    }
  }

  customElements.define('najibs-hero-media', NajibsHeroMedia);
})();
